/**
 * THE PKCE HANDOFF. The ONLY thing this app writes to browser storage, and it is not a token.
 * =========================================================================================
 *
 * WHY ANYTHING IS STORED AT ALL. The authorization-code flow is a **full-page navigation** away to the
 * IdP's login page and back. Every JavaScript variable in the tab is destroyed by that navigation. So
 * the `code_verifier` — which the token exchange needs, and which by construction cannot travel through
 * the redirect where an attacker could read it — has to survive in storage or the flow cannot work.
 * There is no in-memory option. This is the whole reason PKCE implementations touch storage.
 *
 * WHY `sessionStorage`, AND WHAT IS EXPLICITLY NOT ALLOWED HERE:
 *
 *  · **No token is ever written here, or anywhere else on disk.** Access tokens, refresh tokens and id
 *    tokens live in a module-level variable in `oidc.ts` and die with the tab. `src/compose/kiosk.ts`
 *    refuses to persist kiosk payloads for the same reason and states it at length: a durable copy of
 *    sensitive data on a shared field device is readable by whoever uses the device next, with no
 *    ownership check in front of it. A refresh token is strictly worse than a kiosk payload — it is a
 *    long-lived credential — and `discovery/auth/idprism.md` §6 quotes Federate's own governance
 *    naming *"the refresh token … stored in the user agent"* as the threat.
 *  · **What IS written is a single-use nonce.** A `code_verifier` is a random string that is worthless
 *    on its own: it authorizes nothing, identifies nobody, and is only usable in combination with an
 *    authorization code that the IdP will honour exactly once. It is written immediately before a
 *    navigation and {@link TransientStore.take | taken} — read AND deleted in one operation — before
 *    the exchange. Its useful life is the few seconds of the redirect.
 *  · **`sessionStorage`, not `localStorage`**: it is scoped to the one tab and cleared when that tab
 *    closes, so a verifier cannot outlive the sign-in attempt that created it or leak into a second
 *    tab. `localStorage` would survive a browser restart, which for this value is pure downside.
 *  · **`take()` is read-and-clear at the storage layer**, so single use is a property of the store
 *    rather than of every caller remembering to clean up. A replayed callback finds nothing.
 *
 * ⚠️ **A NOTED CONTRADICTION, RESOLVED ON PURPOSE.** `discovery/auth/idprism.md` §7 design implication
 * 5 says *"Do not use `sessionStorage`"*. That guidance is about **the session** — it is arguing that a
 * PWA relaunched from the home screen must find its tokens intact, so tokens must not sit in a store
 * that dies with the tab. It is not about the PKCE verifier. This module follows the security half of
 * that advice (nothing durable) and deliberately declines the convenience half: an expired session
 * here means one more sign-in, whereas a durable refresh token means a stealable one. The cost is
 * stated to the technician in as many words rather than hidden — see `phase.ts`.
 */

/** Everything one sign-in attempt needs to survive the redirect. No token, by construction. */
export interface PkceTransaction {
  /** The `state` we sent. Compared on return; a mismatch aborts. */
  readonly state: string;
  /** The `nonce` we sent. Compared against the id_token claim. */
  readonly nonce: string;
  /** The `code_verifier`. Sent once, to the token endpoint, and never anywhere else. */
  readonly codeVerifier: string;
  /** Epoch ms. Used to reject a callback that arrives absurdly late. */
  readonly startedAt: number;
  /** Echoed to the token endpoint, which requires it to match the authorization request. */
  readonly redirectUri: string;
}

export interface TransientStore {
  put(transaction: PkceTransaction): void;
  /** Read AND delete. The single-use guarantee lives here, not in the caller. */
  take(): PkceTransaction | null;
  /** Read without deleting. For diagnostics only — never for a decision that consumes a code. */
  peek(): PkceTransaction | null;
  clear(): void;
}

/**
 * Ten minutes. Long enough for a slow login with an SMS one-time code and a phone that locks itself
 * mid-flow; short enough that a `code_verifier` found in storage the next morning is discarded rather
 * than used. An authorization code's own lifetime is far shorter than this, so the practical effect is
 * to turn a stale callback into a named state instead of an `invalid_grant`.
 */
export const MAX_TRANSACTION_AGE_MS = 10 * 60 * 1000;

const STORAGE_KEY = 'lockercustody.pkce';

function isTransaction(value: unknown): value is PkceTransaction {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Partial<PkceTransaction>;
  return (
    typeof candidate.state === 'string' &&
    typeof candidate.nonce === 'string' &&
    typeof candidate.codeVerifier === 'string' &&
    typeof candidate.redirectUri === 'string' &&
    typeof candidate.startedAt === 'number'
  );
}

/**
 * The real store.
 *
 * Every access is wrapped: `sessionStorage` **throws** on access in Safari private browsing and when a
 * managed-device policy blocks storage. A throw here would abort the sign-in click with a stack trace;
 * degrading to "no transaction found" produces the `sign-in-failed` state instead, which has words.
 */
export function sessionTransientStore(): TransientStore {
  const storage = (): Storage | null => {
    try {
      return globalThis.sessionStorage ?? null;
    } catch {
      return null;
    }
  };
  return {
    put(transaction) {
      const store = storage();
      if (store === null) {
        throw new Error(
          'sessionStorage is unavailable, so the PKCE code_verifier cannot survive the redirect to the ' +
            'sign-in page. Private browsing or a device storage policy is the usual cause.',
        );
      }
      store.setItem(STORAGE_KEY, JSON.stringify(transaction));
    },
    take() {
      const store = storage();
      if (store === null) {
        return null;
      }
      const raw = store.getItem(STORAGE_KEY);
      // Removed FIRST. If the parse below threw, a retained entry would be re-read on the next
      // callback, which is the replay this store exists to make impossible.
      store.removeItem(STORAGE_KEY);
      return parse(raw);
    },
    peek() {
      const store = storage();
      return store === null ? null : parse(store.getItem(STORAGE_KEY));
    },
    clear() {
      storage()?.removeItem(STORAGE_KEY);
    },
  };
}

/** For tests, and for a runtime with no `sessionStorage` where a same-tab flow is still worth trying. */
export function memoryTransientStore(): TransientStore {
  let held: PkceTransaction | null = null;
  return {
    put(transaction) {
      held = transaction;
    },
    take() {
      const value = held;
      held = null;
      return value;
    },
    peek() {
      return held;
    },
    clear() {
      held = null;
    },
  };
}

function parse(raw: string | null): PkceTransaction | null {
  if (raw === null) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return isTransaction(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** True when the transaction is older than {@link MAX_TRANSACTION_AGE_MS}. */
export function isExpiredTransaction(transaction: PkceTransaction, now: number): boolean {
  return now - transaction.startedAt > MAX_TRANSACTION_AGE_MS;
}
